import { useState, useCallback } from 'react';
import { Alert, Platform } from 'react-native';

type ConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
};

export type ConfirmAsyncOptions = {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Native only: destructive styling for the confirm action */
  destructive?: boolean;
};

export function useConfirmDialog() {
  const [isConfirming, setIsConfirming] = useState(false);

  const confirmAsync = useCallback((options: ConfirmAsyncOptions): Promise<boolean> => {
    const {
      title,
      message,
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      destructive = false,
    } = options;

    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      return Promise.resolve(window.confirm(`${title}\n\n${message}`));
    }

    return new Promise((resolve) => {
      Alert.alert(
        title,
        message,
        [
          {
            text: cancelText,
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: confirmText,
            style: destructive ? 'destructive' : 'default',
            onPress: () => resolve(true),
          },
        ],
        { cancelable: true, onDismiss: () => resolve(false) },
      );
    });
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    const {
      title,
      message,
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      onConfirm,
      onCancel,
    } = options;

    console.log('[useConfirmDialog] Showing confirmation:', title);

    // On web, use window.confirm as it's more reliable
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      const confirmed = window.confirm(`${title}\n\n${message}`);
      if (confirmed) {
        console.log('[useConfirmDialog] User confirmed');
        setIsConfirming(true);
        Promise.resolve(onConfirm())
          .then(() => {
            console.log('[useConfirmDialog] Confirmation action completed');
          })
          .catch((error) => {
            console.error('[useConfirmDialog] Error in confirmation action:', error);
          })
          .finally(() => {
            setIsConfirming(false);
          });
      } else {
        console.log('[useConfirmDialog] User cancelled');
        onCancel?.();
      }
      return;
    }

    // On native, use Alert.alert
    Alert.alert(
      title,
      message,
      [
        {
          text: cancelText,
          style: 'cancel',
          onPress: () => {
            console.log('[useConfirmDialog] User cancelled');
            onCancel?.();
          },
        },
        {
          text: confirmText,
          style: 'destructive',
          onPress: () => {
            console.log('[useConfirmDialog] User confirmed');
            setIsConfirming(true);
            Promise.resolve(onConfirm())
              .then(() => {
                console.log('[useConfirmDialog] Confirmation action completed');
              })
              .catch((error) => {
                console.error('[useConfirmDialog] Error in confirmation action:', error);
              })
              .finally(() => {
                setIsConfirming(false);
              });
          },
        },
      ],
      { cancelable: true }
    );
  }, []);

  return { confirm, confirmAsync, isConfirming };
}

